import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiWarningLight, PiClockCountdownLight, PiRocketLaunchLight, PiXLight } from 'react-icons/pi';
import clsx from 'clsx';
import { UPGRADE_PATH } from "../../../config/entitlementKeys";

/* ─────────────────────────────────────────────────────────
   LICENSING_ENABLED — mirrors the env-var toggle used by
   useLicenseGate. When false, the banner never renders.
   ───────────────────────────────────────────────────────── */
const LICENSING_ENABLED = process.env.REACT_APP_LICENSING_ENABLED === 'true';

/* ─────────────────────────────────────────────────────────
   Helper: pick urgency level from days remaining
   ───────────────────────────────────────────────────────── */
type Urgency = 'critical' | 'warning' | 'info' | null;

function getUrgency(daysRemaining: number | null): Urgency {
  if (daysRemaining === null) return null;
  if (daysRemaining <= 1) return 'critical';
  if (daysRemaining <= 3) return 'critical';
  if (daysRemaining <= 7) return 'warning';
  if (daysRemaining <= 14) return 'info';
  return null;
}

const urgencyStyles: Record<string, string> = {
  critical: 'bg-danger-50 border-danger-200 text-danger-800 dark:bg-danger-900/30 dark:border-danger-800 dark:text-danger-200',
  warning: 'bg-warning-50 border-warning-200 text-warning-800 dark:bg-warning-900/30 dark:border-warning-800 dark:text-warning-200',
  info: 'bg-info-50 border-info-200 text-info-800 dark:bg-info-900/30 dark:border-info-800 dark:text-info-200',
};

const urgencyIcons: Record<string, JSX.Element> = {
  critical: <PiWarningLight className="h-4 w-4 text-danger-500 dark:text-danger-400 flex-shrink-0" />,
  warning: <PiClockCountdownLight className="h-4 w-4 text-warning-500 dark:text-warning-400 flex-shrink-0" />,
  info: <PiClockCountdownLight className="h-4 w-4 text-info-500 dark:text-info-400 flex-shrink-0" />,
};

/* ─────────────────────────────────────────────────────────
   LicenseBanner — renders context-aware banners:
   1. Trial countdown (with urgency)
   2. Subscription expiring soon
   3. Seat limit approaching (≥80%)
   ───────────────────────────────────────────────────────── */
const LicenseBanner: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const access = useSelector((state: RootState) => state.license?.access);
  const loaded = useSelector((state: RootState) => state.license?.loaded ?? false);

  // Dismissed banners (persisted in sessionStorage so they don't nag on every render)
  const [dismissed, setDismissed] = React.useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem('pons_dismissed_banners');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  if (!LICENSING_ENABLED || !loaded || !access?.license_info) return null;

  const info = access.license_info;
  const isTrial = info.plan_type === 'trial';
  const daysRemaining = info.days_remaining;

  const banners: Array<{ key: string; urgency: Urgency; icon: JSX.Element; message: string }> = [];

  // 1. Payment past due warning (admin-only)
  if (info.has_past_due_payment) {
    banners.push({
      key: 'payment-past-due',
      urgency: 'warning',
      icon: <PiWarningLight className="h-4 w-4 text-warning-500 dark:text-warning-400 flex-shrink-0" />,
      message: t('A payment has failed. Please update your payment method to avoid service interruption.'),
    });
  }

  // 2. Trial countdown
  if (isTrial && daysRemaining !== null) {
    const urgency = getUrgency(daysRemaining) ?? 'info';
    const dayLabel = daysRemaining === 1 ? t('day') : t('days');
    banners.push({
      key: 'trial-expiry',
      urgency,
      icon: <PiRocketLaunchLight className="h-4 w-4 flex-shrink-0" />,
      message:
        daysRemaining <= 0
          ? t('Your trial has expired. Upgrade now to continue using PONS.')
          : t('Your trial expires in {{days}} {{dayLabel}}. Upgrade to keep access to all features.', {
              days: daysRemaining,
              dayLabel,
            }),
    });
  }

  // 3. Paid subscription expiring
  if (!isTrial && info.is_expiring_soon && daysRemaining !== null) {
    const urgency = getUrgency(daysRemaining) ?? 'warning';
    banners.push({
      key: 'subscription-expiry',
      urgency,
      icon: urgencyIcons[urgency] || urgencyIcons.warning,
      message: t('Your {{plan}} subscription expires in {{days}} days. Renew to avoid interruption.', {
        plan: info.plan_display_name || info.plan_name,
        days: daysRemaining,
      }),
    });
  }

  // Seat warnings removed — seats are now assignment-driven with no cap.
  // Admins assign freely, billing adjusts automatically via Stripe.

  // Filter dismissed
  const visible = banners.filter((b) => !dismissed.has(b.key));
  if (!visible.length) return null;

  const handleDismiss = (key: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(key);
      sessionStorage.setItem('pons_dismissed_banners', JSON.stringify([...next]));
      return next;
    });
  };

  return (
    <div className="space-y-1 px-4 pt-2">
      {visible.map((banner) => (
        <div
          key={banner.key}
          className={clsx(
            'flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium',
            urgencyStyles[banner.urgency ?? 'info']
          )}
        >
          {banner.icon}
          <span className="flex-1">{banner.message}</span>
          <button
            className="text-xs font-semibold underline hover:no-underline whitespace-nowrap"
            onClick={() => history.push(UPGRADE_PATH)}
          >
            {t('Upgrade')}
          </button>
          <button
            className="p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10"
            onClick={() => handleDismiss(banner.key)}
          >
            <PiXLight className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default LicenseBanner;
